/**
 * Shared operator-API request helper for the Users and Logs tabs. It uses the
 * operator session cookie and maps 401/403 to typed errors, like the other
 * `*Api.ts` files of the console.
 */

/** The operator session is missing or lacks the permission (HTTP 403). */
export class OperatorDenied extends Error {}

/** HTTP 401: the operator session is gone (8 h TTL, no refresh). */
export class OperatorSessionExpired extends OperatorDenied {}

/** The server refused the request with a stable code (for example 400). */
export class OperatorRefused extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
  ) {
    super(code);
  }
}

export type OperatorFetch = typeof fetch;

export async function operatorGet<T>(
  fetcher: OperatorFetch,
  path: string,
): Promise<T> {
  const response = await fetcher(`/operator${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
  if (response.status === 401) throw new OperatorSessionExpired("401");
  if (response.status === 403) throw new OperatorDenied("403");
  if (!response.ok) {
    let code = `operator_http_${String(response.status)}`;
    try {
      const body = (await response.json()) as { detail?: unknown };
      if (typeof body.detail === "string") code = body.detail;
    } catch {
      // keep the generic code
    }
    throw new OperatorRefused(code, response.status);
  }
  return (await response.json()) as T;
}

/** Step-up code the server returns when the operator session is too old. */
export const STEP_UP_REQUIRED = "step_up_required";

/**
 * JSON mutation against the operator API. 401 means the session is gone; a 403
 * with a specific code (step_up_required, role_escalation_denied) is a refusal
 * the caller can explain, while a plain permission 403 is OperatorDenied.
 */
export async function operatorSend<T>(
  fetcher: OperatorFetch,
  method: "POST" | "PATCH",
  path: string,
  body: unknown,
): Promise<T> {
  const response = await fetcher(`/operator${path}`, {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (response.status === 401) throw new OperatorSessionExpired("401");
  if (!response.ok) {
    let detail: unknown;
    try {
      detail = ((await response.json()) as { detail?: unknown }).detail;
    } catch {
      // keep the generic code
    }
    if (
      response.status === 403 &&
      (typeof detail !== "string" || detail === "operator_permission_denied")
    ) {
      throw new OperatorDenied("403");
    }
    throw new OperatorRefused(
      typeof detail === "string"
        ? detail
        : `operator_http_${String(response.status)}`,
      response.status,
    );
  }
  return (await response.json()) as T;
}

export function queryString(
  values: Record<string, string | number | undefined | null>,
): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  }
  const text = params.toString();
  return text ? `?${text}` : "";
}
